import { Rule, RuleAction, RulesModel } from '@modules/configuration/Rules/models/Rules';
import { PresetRule } from '../models/ShippingRulePreset';

// Borrador de regla con id temporal estable para el editor de presets (misma UX que /clients/:id/rules)
export interface PresetRuleDraft extends PresetRule {
  tempId: number;
}

// Convierte el payload del formulario de shipping rules al shape requerido por POST/PUT de presets
export function shippingRuleFormPayloadToPresetRule(payload: Record<string, unknown>): PresetRule {
  const rules = payload.rules as Rule;
  const actionsRaw = payload.actions as RuleAction[];

  const actions = actionsRaw.map((a) => {
    if (a.action === 'courier_integration_id') {
      const valueStr = a.value != null && String(a.value).trim() !== '' ? String(a.value) : '';
      const numericValue = Number(valueStr);
      const serviceId =
        a.courier_service_id != null && a.courier_service_id !== '' ? Number(a.courier_service_id) : NaN;
      return {
        action: 'courier_integration_id' as const,
        value: Number.isFinite(numericValue) ? valueStr : '0',
        ...(Number.isFinite(serviceId) ? { courier_service_id: serviceId } : {}),
      };
    }
    return { action: 'no_enviar' as const, value: '' };
  });

  return {
    name: payload.name as string,
    rules,
    actions,
    is_active: payload.is_active !== false,
  };
}

// Adaptador para reutilizar ClientRuleBoardCard (espera RulesModel)
export function presetDraftToRulesModel(draft: PresetRuleDraft, index: number): RulesModel {
  return {
    id: draft.tempId,
    name: draft.name,
    rules: draft.rules,
    actions: draft.actions as RuleAction[],
    is_active: draft.is_active ?? true,
    priority: index + 1,
    team_id: 0,
    created_at: '',
    updated_at: '',
  };
}

export function stripTempId(draft: PresetRuleDraft): PresetRule {
  const rest = { ...draft };
  delete rest.tempId;
  return rest;
}

interface NormalizeRulesResult {
  rules: PresetRule[];
  error?: string;
}

// Normaliza y valida reglas para cumplir la estructura exacta requerida por POST/PUT de presets.
export function normalizePresetRulesForApi(inputRules: PresetRule[]): NormalizeRulesResult {
  const normalized: PresetRule[] = [];

  for (let ruleIndex = 0; ruleIndex < inputRules.length; ruleIndex += 1) {
    const rule = inputRules[ruleIndex];
    const actions = Array.isArray(rule.actions) ? rule.actions : [];

    if (actions.length === 0) {
      return {
        rules: [],
        error: `Rule at index ${ruleIndex} has invalid actions: actions is empty`,
      };
    }

    const normalizedActions: PresetRule['actions'] = [];

    for (let actionIndex = 0; actionIndex < actions.length; actionIndex += 1) {
      const actionItem = actions[actionIndex];
      const actionType = actionItem?.action;

      if (actionType !== 'courier_integration_id' && actionType !== 'no_enviar') {
        return {
          rules: [],
          error: `Rule at index ${ruleIndex} has invalid actions: invalid action at index ${actionIndex}`,
        };
      }

      if (actionType === 'courier_integration_id') {
        const numericValue = Number(actionItem?.value);
        // value es obligatorio y no puede ser null/undefined/NaN para courier_integration_id
        if (!Number.isFinite(numericValue)) {
          return {
            rules: [],
            error: `Rule at index ${ruleIndex} has invalid actions: courier_integration_id requires numeric value`,
          };
        }
        const serviceId = actionItem?.courier_service_id != null ? Number(actionItem.courier_service_id) : NaN;
        normalizedActions.push({
          action: 'courier_integration_id',
          value: String(numericValue),
          ...(Number.isFinite(serviceId) ? { courier_service_id: serviceId } : {}),
        });
      } else {
        normalizedActions.push({
          action: 'no_enviar',
          value: '',
        });
      }
    }

    normalized.push({
      name: rule.name,
      rules: {
        match_type: rule.rules?.match_type === 'any' ? 'any' : 'all',
        conditions: Array.isArray(rule.rules?.conditions) ? rule.rules.conditions : [],
      },
      actions: normalizedActions,
      is_active: rule.is_active ?? true,
    });
  }

  return { rules: normalized };
}
